import React, { useState } from "react";
import { 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Button, 
  Badge, 
  Row, 
  Col,
  Card,
  CardBody
} from "reactstrap";

const HotspotDetailModal = ({ isOpen, toggle, hotspotData, onApprove, onReject, onSuspend, onVerify, onViewUserInfo, loading }) => {
  const [imageError, setImageError] = useState({ main: false, banner: false });
  
  if (!hotspotData && !loading) return null;

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <Badge color="success" className="px-3 py-2">Approved</Badge>;
      case "rejected":
        return <Badge color="danger" className="px-3 py-2">Rejected</Badge>;
      case "pending":
      default:
        return <Badge color="warning" className="px-3 py-2">Pending</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleImageError = (type) => {
    setImageError(prev => ({ ...prev, [type]: true }));
  };

  // Handle rejection_reason - can be null or an object
  const rejectionReason = hotspotData?.rejection_reason;
  const hasRejectionReason = rejectionReason && typeof rejectionReason === 'object';

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" className="hotspot-detail-modal">
      <ModalHeader className="bg-primary text-white" toggle={toggle}>
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <i className="mdi mdi-map-marker-check me-2"></i>
            <span>
              Hotspot Details - Request #{hotspotData?.request_id || "Loading..."}
              {hotspotData?.tag_id && ` (Tag ID: ${hotspotData.tag_id})`}
            </span>
          </div>
          <div>
            {hotspotData && getStatusBadge(hotspotData.review_status)}
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading tag details...</p>
          </div>
        )}
        {!loading && hotspotData && (
          <>
        {/* Images Section */}
        {(() => {
          const bannerImage = hotspotData.background_image || hotspotData.banner_image;
          const hasImages = hotspotData.image_url || bannerImage;
          
          return hasImages && (
            <Card className="mb-4 shadow-sm">
              <CardBody className="p-3">
                <h6 className="mb-3 fw-bold">
                  <i className="mdi mdi-image me-2"></i>Images
                </h6>
                <div className="d-flex flex-column gap-3">
                  {/* Banner Image - Top */}
                  {bannerImage && (
                    <div>
                      <label className="form-label text-muted small mb-2 d-block">Banner Image</label>
                      {!imageError.banner ? (
                        <img
                          src={bannerImage}
                          alt="Hotspot Banner"
                          className="img-fluid rounded shadow-sm"
                          style={{ 
                            maxHeight: "200px", 
                            width: "100%", 
                            objectFit: "cover",
                            cursor: "pointer"
                          }}
                          onError={() => handleImageError("banner")}
                          onClick={() => window.open(bannerImage, "_blank")}
                          title="Click to view full size"
                        />
                      ) : (
                        <div className="border rounded p-4 text-center text-muted">
                          <i className="mdi mdi-image-off" style={{ fontSize: "3rem" }}></i>
                          <p className="mt-2 mb-0">Banner image failed to load</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Main Image - Square Below */}
                  {hotspotData.image_url && (
                    <div className="d-flex justify-content-start">
                      <div style={{ width: "150px" }}>
                        <label className="form-label text-muted small mb-2 d-block">Main Image</label>
                        {!imageError.main ? (
                          <img
                            src={hotspotData.image_url}
                            alt="Hotspot Main"
                            className="img-fluid rounded shadow-sm"
                            style={{ 
                              width: "150px",
                              height: "150px",
                              objectFit: "cover",
                              cursor: "pointer"
                            }}
                            onError={() => handleImageError("main")}
                            onClick={() => window.open(hotspotData.image_url, "_blank")}
                            title="Click to view full size"
                          />
                        ) : (
                          <div 
                            className="border rounded shadow-sm d-flex align-items-center justify-content-center bg-light"
                            style={{ 
                              width: "150px",
                              height: "150px"
                            }}
                          >
                            <div className="text-center text-muted">
                              <i className="mdi mdi-image-off"></i>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          );
        })()}

        {/* Rejection Reason - Show prominently if rejected */}
        {hasRejectionReason && (
          <Card className="mb-4 border-danger shadow-sm">
            <CardBody className="p-3">
              <h6 className="mb-3 fw-bold text-danger">
                <i className="mdi mdi-alert-circle me-2"></i>Rejection Details
              </h6>
              <div className="bg-danger bg-opacity-10 border border-danger rounded p-3">
                {rejectionReason.comment && (
                  <div className="mb-3">
                    <label className="form-label text-muted small mb-1 fw-bold">Comment:</label>
                    <div className="text-danger">{rejectionReason.comment}</div>
                  </div>
                )}
                {rejectionReason.reasons && Array.isArray(rejectionReason.reasons) && rejectionReason.reasons.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label text-muted small mb-1 fw-bold">Reasons:</label>
                    <ul className="mb-0">
                      {rejectionReason.reasons.map((reason, index) => (
                        <li key={index} className="text-danger">{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <Row className="mt-3 pt-3 border-top border-danger">
                  {rejectionReason.by_admin && (
                    <Col md={6}>
                      <div className="text-muted small">
                        <i className="mdi mdi-account-cog me-1"></i>
                        <strong>Rejected by:</strong> {rejectionReason.by_admin}
                      </div>
                    </Col>
                  )}
                  {rejectionReason.timestamp && (
                    <Col md={6}>
                      <div className="text-muted small">
                        <i className="mdi mdi-clock-outline me-1"></i>
                        <strong>Rejected on:</strong> {formatDate(rejectionReason.timestamp)}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Basic Information */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-3">
            <h6 className="mb-3 fw-bold">
              <i className="mdi mdi-information me-2"></i>Basic Information
            </h6>
            <Row className="g-3">
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Request ID</label>
                  <div className="fw-bold text-primary">#{hotspotData.request_id}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Review Status</label>
                  <div>{getStatusBadge(hotspotData.review_status)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Tag Name</label>
                  <div className="fw-bold fs-6">{hotspotData.tag_name}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Type</label>
                  <div>
                    <Badge color="info" className="px-3 py-2 text-capitalize">{hotspotData.type}</Badge>
                  </div>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Description</label>
                  <div className="p-3 bg-light rounded border">
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {hotspotData.description || <span className="text-muted">No description provided</span>}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Location & Category */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-3">
            <h6 className="mb-3 fw-bold">
              <i className="mdi mdi-map-marker me-2"></i>Location & Category
            </h6>
            <Row className="g-3">
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">
                    <i className="mdi mdi-map-marker-outline me-1"></i>Location
                  </label>
                  <div className="fw-semibold">{hotspotData.location}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Geohash</label>
                  <div className="text-muted font-monospace small">{hotspotData.geohash}</div>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Category</label>
                  <div>
                    <Badge color="secondary" className="px-3 py-2">
                      {hotspotData.category_name} <span className="text-muted">(ID: {hotspotData.category_id})</span>
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* User Information */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 fw-bold">
                <i className="mdi mdi-account me-2"></i>User Information
              </h6>
              {hotspotData.user_id && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => onViewUserInfo && onViewUserInfo(hotspotData.user_id)}
                  className="px-3"
                >
                  <i className="mdi mdi-eye me-1"></i>View Details
                </Button>
              )}
            </div>
            <Row className="g-3">
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">Username</label>
                  <div className="fw-semibold">{hotspotData.username}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">User ID</label>
                  <div className="text-muted font-monospace small">{hotspotData.user_id}</div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Approved Tag Additional Information */}
        {hotspotData.review_status?.toLowerCase() === "approved" && (
          <Card className="mb-4 shadow-sm border-success">
            <CardBody className="p-2 bg-success-subtle">
              <div className="d-flex align-items-center flex-wrap gap-3">
                {hotspotData.tag_id && (
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted small">Tag ID:</span>
                    <Badge color="success" className="px-2">#{hotspotData.tag_id}</Badge>
                  </div>
                )}
                {hotspotData.followers !== undefined && (
                  <div className="d-flex align-items-center gap-1">
                    <i className="mdi mdi-account-group text-muted"></i>
                    <span className="text-muted small">Followers:</span>
                    <span className="fw-semibold">{hotspotData.followers || "0"}</span>
                  </div>
                )}
                {hotspotData.creator_info && hotspotData.creator_info.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    {hotspotData.creator_info[0].image_url && (
                      <img
                        src={hotspotData.creator_info[0].image_url}
                        alt={hotspotData.creator_info[0].username}
                        className="rounded-circle"
                        style={{ width: "24px", height: "24px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <span className="text-muted small">Creator:</span>
                    <span className="fw-semibold">{hotspotData.creator_info[0].username}</span>
                  </div>
                )}
                {hotspotData.pendingRequests && (
                  <Badge color="warning" className="px-2">
                    <i className="mdi mdi-clock-alert me-1"></i>Pending Requests
                  </Badge>
                )}
                {hotspotData.expiring_soon && (
                  <Badge color="danger" className="px-2">
                    <i className="mdi mdi-alert me-1"></i>Expiring Soon
                  </Badge>
                )}
                {hotspotData.is_hidden && (
                  <Badge color="secondary" className="px-2">
                    <i className="mdi mdi-eye-off me-1"></i>Hidden
                  </Badge>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Payment Information - Only show if there's payment data */}
        {(hotspotData.payment_id || hotspotData.transaction_id || (hotspotData.amount && hotspotData.currency) || hotspotData.payment_status || hotspotData.payment_method) && (
          <Card className="mb-4 shadow-sm">
            <CardBody className="p-3">
              <h6 className="mb-3 fw-bold">
                <i className="mdi mdi-credit-card me-2"></i>Payment Information
              </h6>
              <Row className="g-3">
                {hotspotData.payment_id && (
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">Payment ID</label>
                      <div>{hotspotData.payment_id}</div>
                    </div>
                  </Col>
                )}
                {hotspotData.transaction_id && (
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">Transaction ID</label>
                      <div className="text-muted font-monospace small">{hotspotData.transaction_id}</div>
                    </div>
                  </Col>
                )}
                {hotspotData.amount && hotspotData.currency && (
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">Amount</label>
                      <div className="fw-bold text-success">
                        {hotspotData.currency} {hotspotData.amount}
                      </div>
                    </div>
                  </Col>
                )}
                {hotspotData.payment_status && (
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">Payment Status</label>
                      <div>
                        <Badge color={hotspotData.payment_status === "completed" ? "success" : "warning"}>
                          {hotspotData.payment_status}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                )}
                {hotspotData.payment_method && (
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">Payment Method</label>
                      <div>{hotspotData.payment_method}</div>
                    </div>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Additional Information */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="p-3">
            <h6 className="mb-3 fw-bold">
              <i className="mdi mdi-clock-outline me-2"></i>Timeline
            </h6>
            <Row className="g-3">
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label text-muted small mb-1">
                    <i className="mdi mdi-calendar-plus me-1"></i>Created At
                  </label>
                  <div>{formatDate(hotspotData.createdAt)}</div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
          </>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        {!loading && hotspotData && hotspotData.review_status?.toLowerCase() === "pending" && (
          <>
            <Button 
              color="success" 
              onClick={() => onApprove(hotspotData)}
              className="px-4"
            >
              <i className="mdi mdi-check me-2"></i>Approve
            </Button>
            <Button 
              color="danger" 
              onClick={() => onReject(hotspotData)}
              className="px-4"
            >
              <i className="mdi mdi-close me-2"></i>Reject
            </Button>
          </>
        )}
        {!loading && hotspotData && hotspotData.review_status?.toLowerCase() === "approved" && hotspotData.tag_id && (
          <>
            {onSuspend && (
              <Button 
                color="warning" 
                onClick={() => onSuspend(hotspotData)}
                className="px-4"
              >
                <i className="mdi mdi-pause-circle me-2"></i>Suspend
              </Button>
            )}
            {onVerify && (
              <Button 
                color="success" 
                onClick={() => onVerify(hotspotData)}
                className="px-4"
              >
                <i className="mdi mdi-check-circle me-2"></i>Verify
              </Button>
            )}
          </>
        )}
        <Button color="secondary" onClick={toggle} className="px-4">
          <i className="mdi mdi-close me-2"></i>Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default HotspotDetailModal;
